class CustomFAQ {
  constructor(root) {
    this.root = root;

    this.searchInput = this.root.querySelector('[data-faq-search]');
    this.clearButton = this.root.querySelector('[data-faq-clear]');
    this.filters = [...this.root.querySelectorAll('[data-faq-filter]')];
    this.sections = [...this.root.querySelectorAll('[data-faq-section]')];
    this.items = [...this.root.querySelectorAll('[data-faq-item]')];
    this.empty = this.root.querySelector('[data-faq-empty]');
    this.resultCount = this.root.querySelector('[data-faq-result-count]');
    this.toggleAllButton = this.root.querySelector('[data-faq-toggle-all]');

    this.activeFilter = 'all';

    this.searchTokenDocumentFrequency = new Map();
    this.searchItemCount = 0;
    this.minimumSearchScore = Number(this.root.dataset.minimumSearchScore || 25);

    this.allowMultiple = this.root.dataset.closeOthers !== 'true';
    this.autoOpenFirstResult = this.root.dataset.autoOpenFirstResult === 'true';
    this.updateUrl = this.root.dataset.updateUrl === 'true';

    this.openAllLabel =
      this.toggleAllButton?.dataset.openLabel ||
      this.root.dataset.openAllLabel ||
      'Alle öffnen';

    this.closeAllLabel =
      this.toggleAllButton?.dataset.closeLabel ||
      this.root.dataset.closeAllLabel ||
      'Alle schließen';

    this.resultSingularLabel =
      this.root.dataset.resultSingularLabel ||
      'Frage';

    this.resultPluralLabel =
      this.root.dataset.resultPluralLabel ||
      'Fragen';
    
    this.searchDebounceDelay = Number(this.root.dataset.searchDebounceDelay || 650);
    this.searchDebounceTimer = null;
    this.isComposing = false;

    this.init();
  }

  init() {
    this.sortElements();
    this.buildSearchTokenStats();
    this.setInitialStateFromUrl();
    this.bindSearch();
    this.bindClear();
    this.bindFilters();
    this.bindItems();
    this.bindToggleAll();
    this.updateView({ shouldAutoOpen: false });
  }

  sortElements() {
    this.sortContainerChildren(this.root, '[data-faq-filter]');
    this.sortContainerChildren(this.root, '[data-faq-section]');

    this.sections.forEach((section) => {
      this.sortContainerChildren(section, '[data-faq-item]');
    });

    this.filters = [...this.root.querySelectorAll('[data-faq-filter]')];
    this.sections = [...this.root.querySelectorAll('[data-faq-section]')];
    this.items = [...this.root.querySelectorAll('[data-faq-item]')];
  }

  sortContainerChildren(container, selector) {
    const children = [...container.querySelectorAll(selector)];

    if (!children.length) return;

    const parent = children[0].parentElement;

    children
      .sort((a, b) => {
        const sortA = Number(a.dataset.sort || 999);
        const sortB = Number(b.dataset.sort || 999);

        return sortA - sortB;
      })
      .forEach((child) => {
        parent.appendChild(child);
      });
  }

  bindSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('compositionstart', () => {
      this.isComposing = true;
    });

    this.searchInput.addEventListener('compositionend', () => {
      this.isComposing = false;
      this.scheduleSearchUpdate({ shouldAutoOpen: true });
    });

    this.searchInput.addEventListener('input', () => {
      if (this.isComposing) return;

      this.scheduleSearchUpdate({ shouldAutoOpen: true });
    });

    this.searchInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;

      event.preventDefault();
      this.applySearchUpdate({ shouldAutoOpen: true });
    });

    this.searchInput.addEventListener('search', () => {
      this.applySearchUpdate({ shouldAutoOpen: true });
    });
  }

  scheduleSearchUpdate({ shouldAutoOpen = true } = {}) {
    window.clearTimeout(this.searchDebounceTimer);

    const hasQuery = this.searchInput?.value.trim().length > 0;

    if (hasQuery && this.activeFilter !== 'all') {
      this.setActiveFilter('all');
    }

    if (!hasQuery) {
      this.applySearchUpdate({ shouldAutoOpen: false });
      return;
    }

    this.setSearchPending(true);

    this.searchDebounceTimer = window.setTimeout(() => {
      this.applySearchUpdate({ shouldAutoOpen });
    }, this.searchDebounceDelay);
  }

  applySearchUpdate({ shouldAutoOpen = true } = {}) {
    window.clearTimeout(this.searchDebounceTimer);

    this.searchDebounceTimer = null;

    const hasQuery = this.searchInput?.value.trim().length > 0;

    if (hasQuery && this.activeFilter !== 'all') {
      this.setActiveFilter('all');
    }

    this.setSearchPending(false);
    this.updateView({ shouldAutoOpen });
    this.updateUrlState();
  }

  setSearchPending(isPending) {
    this.root.classList.toggle('is-searching', isPending);
    this.root.setAttribute('aria-busy', String(isPending));

    if (this.clearButton && this.searchInput) {
      const hasValue = this.searchInput.value.trim().length > 0;

      this.clearButton.classList.toggle('is-visible', hasValue);
    }
  }

  bindClear() {
    if (!this.clearButton || !this.searchInput) return;

    this.clearButton.addEventListener('click', () => {
      window.clearTimeout(this.searchDebounceTimer);

      this.searchDebounceTimer = null;
      this.searchInput.value = '';
      this.searchInput.focus();

      this.setSearchPending(false);
      this.updateView({ shouldAutoOpen: false });
      this.updateUrlState();
    });
  }

  bindFilters() {
    this.filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        if (filter.hidden) return;

        this.setActiveFilter(filter.dataset.faqFilter || 'all');
        this.updateView({ shouldAutoOpen: false });
        this.updateUrlState();
      });
    });
  }

    bindItems() {
    this.items.forEach((item) => {
      const button = item.querySelector('[data-faq-button]');

      if (!button) return;

      button.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        if (isOpen) {
          this.closeItem(item);

          window.requestAnimationFrame(() => {
            this.updateToggleAllLabel();
          });

          return;
        }

        if (!this.allowMultiple) {
          this.getVisibleItems().forEach((currentItem) => {
            if (currentItem !== item) {
              this.closeItem(currentItem);
            }
          });
        }

        this.openItem(item);

        window.requestAnimationFrame(() => {
          this.updateToggleAllLabel();
        });
      });
    });
  }

  bindToggleAll() {
    if (!this.toggleAllButton) return;

    this.toggleAllButton.addEventListener('click', () => {
      const visibleItems = this.getVisibleItems();

      const hasOpenItem = visibleItems.some((item) => {
        return item.classList.contains('is-open');
      });

      if (hasOpenItem) {
        visibleItems.forEach((item) => {
          this.closeItem(item);
        });
      } else {
        visibleItems.forEach((item) => {
          this.openItem(item);
        });
      }

      window.requestAnimationFrame(() => {
        this.updateToggleAllLabel();
      });
    });
  }

  setInitialStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('faq-search');
    const filterGroup = params.get('faq-group') || params.get('faq-category');

    if (query && this.searchInput) {
      this.searchInput.value = query;
    }

    if (filterGroup) {
      const matchingFilter = this.filters.find((filter) => {
        return filter.dataset.faqFilter === filterGroup;
      });

      if (matchingFilter) {
        this.activeFilter = filterGroup;
      }
    }

    this.updateActiveFilterUI();
  }

  updateUrlState() {
    if (!this.updateUrl) return;

    const url = new URL(window.location.href);
    const params = url.searchParams;
    const query = this.searchInput?.value.trim() || '';

    params.delete('faq-category');

    if (query) {
      params.set('faq-search', query);
    } else {
      params.delete('faq-search');
    }

    if (this.activeFilter && this.activeFilter !== 'all') {
      params.set('faq-group', this.activeFilter);
    } else {
      params.delete('faq-group');
    }

    window.history.replaceState({}, '', url.toString());
  }

  updateView({ shouldAutoOpen = false } = {}) {
    const tokens = this.getQueryTokens();
    const hasQuery = this.getRawQuery().length > 0;
    let visibleCount = 0;

    this.updateCounts(tokens);

    if (this.clearButton) {
      this.clearButton.classList.toggle('is-visible', this.searchInput?.value.trim().length > 0);
    }

    this.items.forEach((item) => {
      const score = this.getItemSearchScore(item, tokens);
      const matchesFilter = this.itemMatchesFilter(item);
      const isVisible = matchesFilter && this.passesMinimumSearchScore(score, hasQuery);

      item.dataset.searchScore = String(score);

      this.setItemVisible(item, isVisible);

      if (isVisible) {
        visibleCount += 1;
      }
    });

    this.sections.forEach((section) => {
      const visibleItems = [...section.querySelectorAll('[data-faq-item]')].filter((item) => {
        return !item.hidden;
      });

      const sectionScore = visibleItems.reduce((sum, item) => {
        return sum + Number(item.dataset.searchScore || 0);
      }, 0);

      const sectionMaxScore = visibleItems.reduce((max, item) => {
        return Math.max(max, Number(item.dataset.searchScore || 0));
      }, 0);

      section.dataset.visibleCount = String(visibleItems.length);
      section.dataset.searchScore = String(sectionScore);
      section.dataset.maxSearchScore = String(sectionMaxScore);

      this.setSectionVisible(section, visibleItems.length > 0);
    });

    this.sortResultsByRelevance(hasQuery);

    if (this.empty) {
      this.empty.hidden = visibleCount > 0;
    }

    this.updateResultCount(visibleCount);
    this.updateToggleAllLabel();

    if (shouldAutoOpen && this.autoOpenFirstResult && hasQuery && visibleCount > 0) {
      const firstVisible = this.getVisibleItems()[0];

      this.items.forEach((item) => {
        if (item !== firstVisible) {
          this.closeItem(item, false);
        }
      });

      this.openItem(firstVisible);
    }
  }

  updateCounts(tokens = this.getQueryTokens()) {
    const counts = Object.create(null);
    let totalCount = 0;

    this.items.forEach((item) => {
      if (!this.itemMatchesSearch(item, tokens)) return;

      const filterGroup = this.getItemFilterGroup(item);

      if (!filterGroup) return;

      counts[filterGroup] = (counts[filterGroup] || 0) + 1;
      totalCount += 1;
    });

    counts.all = totalCount;

    if (this.activeFilter !== 'all' && (counts[this.activeFilter] || 0) === 0) {
      this.setActiveFilter('all');
    }

    this.filters.forEach((filter) => {
      const filterValue = filter.dataset.faqFilter || 'all';
      const isAllFilter = filterValue === 'all';
      const count = isAllFilter ? totalCount : counts[filterValue] || 0;
      const countElement = filter.querySelector('[data-faq-filter-count]');

      if (countElement) {
        countElement.textContent = String(count);
      }

      const shouldHide = !isAllFilter && count === 0;

      filter.hidden = shouldHide;
      filter.classList.toggle('is-hidden', shouldHide);
      filter.setAttribute('aria-hidden', String(shouldHide));

      if (shouldHide) {
        filter.setAttribute('tabindex', '-1');
      } else {
        filter.removeAttribute('tabindex');
      }
    });

    this.updateActiveFilterUI();

    return counts;
  }

  updateResultCount(count) {
    if (!this.resultCount) return;

    const label = count === 1 ? this.resultSingularLabel : this.resultPluralLabel;

    this.resultCount.textContent = `${count} ${label}`;
  }

  updateToggleAllLabel() {
    if (!this.toggleAllButton) return;

    const visibleItems = this.getVisibleItems();

    if (!visibleItems.length) {
      this.toggleAllButton.hidden = true;
      return;
    }

    this.toggleAllButton.hidden = false;

    const hasOpenItem = visibleItems.some((item) => {
      return item.classList.contains('is-open');
    });

    this.toggleAllButton.textContent = hasOpenItem ? this.closeAllLabel : this.openAllLabel;
  }

  setActiveFilter(value = 'all') {
    this.activeFilter = value || 'all';
    this.updateActiveFilterUI();
  }

  updateActiveFilterUI() {
    this.filters.forEach((filter) => {
      const filterValue = filter.dataset.faqFilter || 'all';
      const isActive = filterValue === this.activeFilter;

      filter.classList.toggle('is-active', isActive);
      filter.setAttribute('aria-pressed', String(isActive));
    });
  }

  itemMatches(item, tokens) {
    if (!this.itemMatchesFilter(item)) return false;

    return this.itemMatchesSearch(item, tokens);
  }

  itemMatchesFilter(item) {
    const filterGroup = this.getItemFilterGroup(item);

    return this.activeFilter === 'all' || filterGroup === this.activeFilter;
  }

  itemMatchesSearch(item, tokens = this.getQueryTokens()) {
    const query = this.getRawQuery();

    if (!query) return true;

    return this.getItemSearchScore(item, tokens) > 0;
  }

  buildSearchTokenStats() {
    this.searchTokenDocumentFrequency = new Map();
    this.searchItemCount = this.items.length;

    this.items.forEach((item) => {
      const text = this.normalize(`${item.dataset.search || ''} ${item.dataset.keywords || ''}`);
      const uniqueTokens = new Set(
        text
          .split(' ')
          .map((token) => token.trim())
          .filter(Boolean)
          .filter((token) => token.length >= 4)
      );

      uniqueTokens.forEach((token) => {
        this.searchTokenDocumentFrequency.set(
          token,
          (this.searchTokenDocumentFrequency.get(token) || 0) + 1
        );
      });
    });
  }

  getTokenWeight(token) {
    if (!token || token.length < 4 || !this.searchItemCount) return 0;

    const frequency = this.searchTokenDocumentFrequency.get(token) || 0;
    const ratio = frequency / this.searchItemCount;

    if (ratio >= 0.35) return 0;
    if (ratio >= 0.2) return 0.2;
    if (ratio >= 0.1) return 0.55;

    return 1;
  }

  passesMinimumSearchScore(score, hasQuery) {
    if (!hasQuery) return true;

    return score >= this.minimumSearchScore;
  }

  getItemSearchScore(item, tokens = this.getQueryTokens()) {
    const query = this.getRawQuery();

    if (!query) return 1;

    const index = this.normalize(item.dataset.search || '');
    const keywords = this.getKeywords(item.dataset.keywords || '');
    const indexWords = index.split(' ').filter(Boolean);

    let score = 0;

    if (index.includes(query)) {
      score += 160;
    }

    keywords.forEach((keyword) => {
      score += this.getKeywordMatchScore(keyword, query, tokens);
    });

    tokens.forEach((queryToken) => {
      if (queryToken.length < 4) return;

      const tokenWeight = this.getTokenWeight(queryToken);

      if (tokenWeight <= 0) return;

      let bestTokenScore = 0;

      indexWords.forEach((indexWord) => {
        if (indexWord.length < 4) return;

        bestTokenScore = Math.max(
          bestTokenScore,
          this.getTokenMatchScore(indexWord, queryToken)
        );
      });

      score += bestTokenScore * tokenWeight;
    });

    return Math.round(score);
  }

  getKeywordMatchScore(keyword, query, queryTokens) {
    if (!keyword || !query) return 0;

    if (keyword === query) {
      return 140;
    }

    if (query.includes(keyword)) {
      return keyword.length >= 5 ? 110 : 35;
    }

    if (query.length >= 5 && keyword.includes(query)) {
      return 95;
    }

    const keywordTokens = keyword
      .split(' ')
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => token.length >= 3);

    if (!keywordTokens.length) return 0;

    let totalScore = 0;
    let matchedKeywordTokens = 0;

    keywordTokens.forEach((keywordToken) => {
      let bestTokenScore = 0;

      queryTokens.forEach((queryToken) => {
        const tokenWeight = this.getTokenWeight(queryToken);

        if (tokenWeight <= 0 && keywordToken !== queryToken) return;

        bestTokenScore = Math.max(
          bestTokenScore,
          this.getTokenMatchScore(keywordToken, queryToken) * Math.max(tokenWeight, 0.35)
        );
      });

      if (bestTokenScore > 0) {
        matchedKeywordTokens += 1;
        totalScore += bestTokenScore;
      }
    });

    if (keywordTokens.length > 1 && matchedKeywordTokens === keywordTokens.length) {
      totalScore += 45;
    }

    return totalScore;
  }

  getTokenMatchScore(a, b) {
    if (!a || !b) return 0;

    if (a === b) {
      return 35;
    }

    if (
      a.length >= 4 &&
      b.length >= 4 &&
      (a.includes(b) || b.includes(a))
    ) {
      return 22;
    }

    if (this.isFuzzyMatch(a, b)) {
      return 10;
    }

    return 0;
  }

  sortResultsByRelevance(hasQuery) {
    if (!hasQuery) {
      this.restoreDefaultOrder();
      return;
    }

    this.sections.forEach((section) => {
      const itemsContainer = section.querySelector('.custom-faq__items');

      if (!itemsContainer) return;

      const items = [...section.querySelectorAll('[data-faq-item]')];

      items
        .sort((a, b) => {
          const scoreA = Number(a.dataset.searchScore || 0);
          const scoreB = Number(b.dataset.searchScore || 0);
          const sortA = Number(a.dataset.sort || 999);
          const sortB = Number(b.dataset.sort || 999);

          if (scoreB !== scoreA) return scoreB - scoreA;

          return sortA - sortB;
        })
        .forEach((item) => {
          itemsContainer.appendChild(item);
        });
    });

    const sectionsContainer =
      this.root.querySelector('.custom-faq__sections') ||
      this.sections[0]?.parentElement;

    if (!sectionsContainer) return;

    this.sections
      .sort((a, b) => {
        const visibleA = Number(a.dataset.visibleCount || 0);
        const visibleB = Number(b.dataset.visibleCount || 0);
        const maxScoreA = Number(a.dataset.maxSearchScore || 0);
        const maxScoreB = Number(b.dataset.maxSearchScore || 0);
        const scoreA = Number(a.dataset.searchScore || 0);
        const scoreB = Number(b.dataset.searchScore || 0);
        const sortA = Number(a.dataset.sort || 999);
        const sortB = Number(b.dataset.sort || 999);

        if (maxScoreB !== maxScoreA) return maxScoreB - maxScoreA;
        if (scoreB !== scoreA) return scoreB - scoreA;
        if (visibleB !== visibleA) return visibleB - visibleA;

        return sortA - sortB;
      })
      .forEach((section) => {
        sectionsContainer.appendChild(section);
      });
  }

  restoreDefaultOrder() {
    const sectionsContainer =
      this.root.querySelector('.custom-faq__sections') ||
      this.sections[0]?.parentElement;

    if (sectionsContainer) {
      this.sections
        .sort((a, b) => {
          const sortA = Number(a.dataset.sort || 999);
          const sortB = Number(b.dataset.sort || 999);

          return sortA - sortB;
        })
        .forEach((section) => {
          sectionsContainer.appendChild(section);
        });
    }

    this.sections.forEach((section) => {
      const itemsContainer = section.querySelector('.custom-faq__items');

      if (!itemsContainer) return;

      [...section.querySelectorAll('[data-faq-item]')]
        .sort((a, b) => {
          const sortA = Number(a.dataset.sort || 999);
          const sortB = Number(b.dataset.sort || 999);

          return sortA - sortB;
        })
        .forEach((item) => {
          itemsContainer.appendChild(item);
        });
    });
  }

  keywordStringMatchesQuery(keywordString, query, queryTokens) {
    const keywords = this.getKeywords(keywordString);

    if (!keywords.length) return false;

    return keywords.some((keyword) => {
      return this.keywordMatchesQuery(keyword, query, queryTokens);
    });
  }

  getKeywords(keywordString) {
    return String(keywordString || '')
      .split(/[,;\n|]+/)
      .map((keyword) => this.normalize(keyword))
      .filter(Boolean);
  }

  keywordMatchesQuery(keyword, query, queryTokens) {
    if (!keyword || !query) return false;

    if (query.includes(keyword)) {
      return true;
    }

    if (query.length >= 5 && keyword.includes(query)) {
      return true;
    }

    const keywordTokens = keyword
      .split(' ')
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => token.length >= 3);

    let strongMatches = 0;
    let weakMatches = 0;

    keywordTokens.forEach((keywordToken) => {
      queryTokens.forEach((queryToken) => {
        if (!this.tokensAreClose(keywordToken, queryToken)) return;

        if (keywordToken.length >= 5 || queryToken.length >= 5) {
          strongMatches += 1;
        } else {
          weakMatches += 1;
        }
      });
    });

    return strongMatches >= 1 || weakMatches >= 2;
  }

  tokensAreClose(keywordToken, queryToken) {
    if (!keywordToken || !queryToken) return false;

    if (keywordToken === queryToken) {
      return true;
    }

    if (
      keywordToken.length >= 4 &&
      queryToken.length >= 4 &&
      (keywordToken.includes(queryToken) || queryToken.includes(keywordToken))
    ) {
      return true;
    }

    return this.isFuzzyMatch(keywordToken, queryToken);
  }

  isFuzzyMatch(a, b) {
    if (!a || !b) return false;
    if (a.length < 5 || b.length < 5) return false;

    const lengthDifference = Math.abs(a.length - b.length);

    if (lengthDifference > 2) return false;

    const distance = this.levenshteinDistance(a, b);
    const maxDistance = Math.max(a.length, b.length) >= 8 ? 2 : 1;

    return distance <= maxDistance;
  }

  levenshteinDistance(a, b) {
    const matrix = [];

    for (let i = 0; i <= b.length; i += 1) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i += 1) {
      for (let j = 1; j <= a.length; j += 1) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }

  getRawQuery() {
    return this.normalize(this.searchInput?.value || '');
  }

  getQueryTokens() {
    const query = this.getRawQuery();

    if (!query) return [];

    return query
      .split(' ')
      .map((token) => token.trim())
      .filter(Boolean)
      .filter((token) => token.length > 1);
  }

  getItemFilterGroup(item) {
    return item.dataset.filterGroup || item.dataset.category || '';
  }

  setItemVisible(item, isVisible) {
    const button = item.querySelector('[data-faq-button]');

    if (isVisible) {
      item.hidden = false;
      item.classList.remove('is-hidden');
      item.setAttribute('aria-hidden', 'false');

      if (button) {
        button.tabIndex = 0;
      }

      return;
    }

    this.closeItem(item, false);

    item.classList.add('is-hidden');
    item.setAttribute('aria-hidden', 'true');
    item.hidden = true;

    if (button) {
      button.tabIndex = -1;
    }
  }

  setSectionVisible(section, isVisible) {
    if (isVisible) {
      section.hidden = false;
      section.classList.remove('is-hidden');
      section.setAttribute('aria-hidden', 'false');
      return;
    }

    section.classList.add('is-hidden');
    section.setAttribute('aria-hidden', 'true');
    section.hidden = true;
  }

  openItem(item, shouldAnimate = true) {
    if (!item || item.hidden || item.classList.contains('is-open')) return;

    const button = item.querySelector('[data-faq-button]');
    const panel = item.querySelector('[data-faq-panel]');

    if (!panel) return;

    item.classList.add('is-open');

    if (button) {
      button.setAttribute('aria-expanded', 'true');
    }

    if (!shouldAnimate) {
      panel.style.height = 'auto';
      return;
    }

    panel.style.height = '0px';

    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height') return;

      panel.removeEventListener('transitionend', onTransitionEnd);

      if (item.classList.contains('is-open')) {
        panel.style.height = 'auto';
      }
    };

    panel.addEventListener('transitionend', onTransitionEnd);
  }

  closeItem(item, shouldAnimate = true) {
    if (!item || !item.classList.contains('is-open')) return;

    const button = item.querySelector('[data-faq-button]');
    const panel = item.querySelector('[data-faq-panel]');

    if (!panel) return;

    if (button) {
      button.setAttribute('aria-expanded', 'false');
    }

    if (!shouldAnimate) {
      item.classList.remove('is-open');
      panel.style.height = '0px';
      return;
    }

    panel.style.height = `${panel.scrollHeight}px`;

    requestAnimationFrame(() => {
      item.classList.remove('is-open');
      panel.style.height = '0px';
    });
  }

  getVisibleItems() {
    return this.items.filter((item) => {
      return !item.hidden;
    });
  }

  normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}

function initCustomFAQ(scope = document) {
  const roots = [...scope.querySelectorAll('[data-custom-faq]')];

  roots.forEach((root) => {
    if (root.dataset.customFaqInitialized === 'true') return;

    root.dataset.customFaqInitialized = 'true';
    new CustomFAQ(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCustomFAQ();
  });
} else {
  initCustomFAQ();
}

document.addEventListener('shopify:section:load', (event) => {
  initCustomFAQ(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  initCustomFAQ(event.target);
});